import ApexCharts from 'apexcharts'
import { ApexOptions } from 'apexcharts'
import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { CostTransactionStackChartResponse } from 'modules/cost/api/types'
import { useIsCycleBeforeThreshold } from 'modules/pond/stores/selector'
import React from 'react'
import ReactApexChart from 'react-apexcharts'
import { FONT_PRIMARY } from 'theme/typography'

import { Box, Skeleton, Stack } from '@mui/material'
import { useTheme } from '@mui/material'

import ChartFallback from 'components/ChartFallback'

// Computes a nice round max and tick count for y-axis
// e.g. max=350,000 → { max: 400,000, tickAmount: 4 }
function getNiceYAxis(max: number, tickCount = 5): { max: number; tickAmount: number } {
  if (max <= 0) return { max: tickCount * 100, tickAmount: tickCount }
  const roughInterval = max / tickCount
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)))
  const ratio = roughInterval / magnitude
  let interval: number
  if (ratio <= 1) interval = magnitude
  else if (ratio <= 2) interval = 2 * magnitude
  else if (ratio <= 5) interval = 5 * magnitude
  else interval = 10 * magnitude
  const niceMax = Math.ceil(max / interval) * interval
  return { max: niceMax, tickAmount: Math.round(niceMax / interval) }
}

type Props = {
  data?: CostTransactionStackChartResponse[]
  getColor: (idx: number, provided?: string) => string
  paletteColors: string[]
  t: (key: string, def?: string) => string
  loading?: boolean
}

const StackedChart: React.FC<Props> = ({ data, getColor, paletteColors, t, loading }) => {
  const theme = useTheme()
  const label = useDefaultValueLabel()
  const chartRef = React.useRef<ReactApexChart | null>(null)
  const [hiddenSeries, setHiddenSeries] = React.useState<string[]>([])
  const isCycleBeforeThreshold = useIsCycleBeforeThreshold()

  const { series, options } = React.useMemo(() => {
    if (!Array.isArray(data) || !data.length) {
      return { series: [], options: {} as ApexOptions }
    }

    const sortedData = [...data].sort(
      (a, b) => new Date(a.date_time).getTime() - new Date(b.date_time).getTime()
    )

    const categories = Array.from(
      sortedData
        .flatMap((r) =>
          (r.cost || []).map((c) => ({
            id: String(c.id ?? c.name),
            name: c.name ?? (c as any).label,
            color: (c as any).color,
          }))
        )
        .reduce((m: Map<string, any>, c) => m.set(c.id, c), new Map())
        .values()
    )

    const visibleCategories = categories.filter((cat) => !hiddenSeries.includes(cat.name))

    if (!visibleCategories.length) {
      return { series: [], options: {} as ApexOptions }
    }

    const filteredData = sortedData.filter((row) =>
      visibleCategories.some((cat) => {
        const value = Number(
          (row.cost || []).find((c) => String(c.id ?? c.name) === cat.id)?.total_amount ?? 0
        )
        return value > 0
      })
    )

    const series = categories.map((cat, i) => ({
      name: cat.name ?? `Item ${i + 1}`,
      data: filteredData.map((row) => ({
        x: row.age,
        y: hiddenSeries.includes(cat.name)
          ? 0
          : Number(
              (row.cost || []).find((c) => String(c.id ?? c.name) === cat.id)?.total_amount ?? 0
            ),
      })),
    }))

    const numPoints = series[0]?.data.length ?? 0
    const maxStackedValue = Array.from({ length: numPoints }, (_, i) =>
      series.reduce((sum, s) => sum + ((s.data[i] as any)?.y ?? 0), 0)
    ).reduce((max, total) => Math.max(max, total), 0)

    const niceYAxis = getNiceYAxis(maxStackedValue)

    const maxAge = Math.max(...sortedData.map((r) => (r as any).age ?? 0))
    const xTickAmount = Math.min(10, maxAge - 1)

    const colors = categories.map((c, idx) => getColor(idx, c.color))

    const options: ApexOptions = {
      chart: {
        id: 'cost-stacked-chart',
        type: 'bar',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: FONT_PRIMARY,
        animations: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth:
            filteredData.length === 1
              ? '20%'
              : filteredData.length <= 3
              ? '30%'
              : filteredData.length <= 6
              ? '45%'
              : '65%',
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        type: 'numeric',
        min: 1,
        max: maxAge,
        tickAmount: xTickAmount,
        labels: {
          formatter: (val: string) => String(Math.round(Number(val))),
          style: { fontSize: '11px' },
        },
        axisTicks: { show: false },
        title: {
          text: t('age.with.unit'),
          style: { fontSize: '12px', fontFamily: FONT_PRIMARY, fontWeight: 400 },
        },
        crosshairs: { show: false },
      },
      yaxis: {
        min: 0,
        max: niceYAxis.max,
        tickAmount: niceYAxis.tickAmount,
        labels: {
          formatter: (val: number) => `฿${Math.round(val).toLocaleString()}`,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, dataPointIndex, w }) {
          const age = Math.round(w.globals.seriesX[0][dataPointIndex])

          let content = `
            <div style="padding:8px 10px;">
                <div style="font-weight:600;margin-bottom:6px;">
                    ${t('age')} ${age} ${t('unit.day')}
                </div>
        `

          const total = series.reduce((sum: number, s: number[]) => sum + (s[dataPointIndex] ?? 0), 0)

          series.forEach((s: number[], idx: number) => {
            const value = s[dataPointIndex]

            if (!value || value === 0) return

            const color = w.globals.colors[idx]
            const name = label.costCategory(w.globals.seriesNames[idx])

            content += `
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                    <span style="
                        width:10px;
                        height:10px;
                        border-radius:2px;
                        background:${color};
                        display:inline-block;
                    "></span>
                    <span>${name}:</span>
                    <span style="margin-left:auto;font-weight:500;">
                        ฿${Number(value).toLocaleString()}
                    </span>
                </div>
            `
          })

          content += `
                <div style="display:flex;align-items:center;gap:6px;margin-top:6px;padding-top:6px;border-top:1px solid #e0e0e0;">
                    <span style="font-weight:600;">${t('summary.cost.total.label')}:</span>
                    <span style="margin-left:auto;font-weight:700;">
                        ฿${Number(total).toLocaleString()}
                    </span>
                </div>
          `

          content += `</div>`

          return content
        },
      },
      grid: {
        borderColor: (theme.palette as any)?.grey?.[200] ?? theme.palette.divider,
      },
      states: {
        hover: { filter: { type: 'lighten', value: 0.1 } },
        active: { filter: { type: 'darken', value: 0.1 } },
      },
      colors,
    }

    return { series, options }
  }, [data, getColor, theme, t, hiddenSeries, label])

  const toggleSeries = (seriesName: string) => {
    const isHidden = hiddenSeries.includes(seriesName)

    if (isHidden) {
      ApexCharts.exec('cost-stacked-chart', 'showSeries', seriesName)
      setHiddenSeries((prev) => prev.filter((s) => s !== seriesName))
    } else {
      ApexCharts.exec('cost-stacked-chart', 'hideSeries', seriesName)
      setHiddenSeries((prev) => [...prev, seriesName])
    }
  }

  if (loading)
    return (
      <Box>
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-around"
          height={300}
          px={2}
        >
          {[60, 80, 45, 90, 70, 55, 85].map((h, i) => (
            <Skeleton key={i} variant="rounded" width={32} height={`${h}%`} />
          ))}
        </Stack>
        <Stack direction="row" justifyContent="space-around" mt={1} px={2}>
          {[40, 50, 35, 55, 45, 40, 50].map((w, i) => (
            <Skeleton key={i} variant="text" width={w} />
          ))}
        </Stack>
      </Box>
    )

  if (isCycleBeforeThreshold)
      return <ChartFallback height={350} />;

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {series.map((s, index) => {
          const color = options.colors?.[index] || theme.palette.primary.main
          const isHidden = hiddenSeries.includes(s.name)

          return (
            <Box
              key={s.name}
              onClick={() => toggleSeries(s.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                opacity: isHidden ? 0.4 : 1,
                transition: '0.2s',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '4px',
                  backgroundColor: color,
                }}
              />
              <Box sx={{ fontSize: 14 }}>{label.costCategory(s.name)}</Box>
            </Box>
          )
        })}
      </Box>

      <ReactApexChart ref={chartRef} options={options} series={series} type="bar" height={350} />
    </Box>
  )
}

export default StackedChart
